import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFormHeaderTranslations } from '@common/i18n/formHeader';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { useEffect, useRef, useState } from 'react';

interface Props {
    timeout?: number; // total inactivity time before logout
    warningTime?: number; // time before timeout to show popup
    onTimeout: () => void;
    language?: TemplateFormLanguage;
}

export default function ActivityTracker({
    timeout = 5 * 60 * 1000, // 5 minutes
    warningTime = 60 * 1000, // show popup 1 minute before timeout
    onTimeout,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const lastActivityRef = useRef(Date.now());
    const [showPopup, setShowPopup] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const sessionT = getFormHeaderTranslations(language).session;

    const resetTimer = () => {
        lastActivityRef.current = Date.now();
        setShowPopup(false);
    };

    useEffect(() => {
        const handleActivity = () => resetTimer();
        const events = ['mousemove', 'keydown', 'scroll', 'touchstart'];

        events.forEach((event) => void window.addEventListener(event, handleActivity));

        const interval = setInterval(() => {
            const diff = Date.now() - lastActivityRef.current;

            if (diff >= timeout) {
                onTimeout();
                resetTimer();
            } else if (diff >= timeout - warningTime) {
                const secondsLeft = Math.ceil((timeout - diff) / 1000);
                setRemainingSeconds(secondsLeft);
                setShowPopup(true);
            }
        }, 1000);

        return () => {
            events.forEach((event) => void window.removeEventListener(event, handleActivity));
            clearInterval(interval);
        };
    }, [timeout, warningTime, onTimeout]);

    return (
        <>
            {showPopup && (
                <div
                    style={{
                        position: 'fixed',
                        top: '20%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'white',
                        padding: '20px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        borderRadius: '8px',
                        zIndex: 9999,
                        textAlign: 'center',
                    }}
                >
                    <h2>{sessionT.timeoutWarningTitle}</h2>
                    <p>{sessionT.timeoutWarningMessage(remainingSeconds)}</p>
                </div>
            )}
        </>
    );
}
